import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  createStudentInvoice,
  getPaymentChecklist,
  getStudentInvoicePdfBlob,
  previewStudentInvoice,
} from '../../api/students';
import { useAuth } from '../../context/authContext';
import { downloadBlob } from '../../utils/downloadBlob';
import { PaymentItemStatus } from '../../types/payment';
import { InvoiceType } from '../../types/student';
import { ExchangeRatePanel } from './ExchangeRatePanel';
import { ExchangeRateSelect } from '../../components/ExchangeRateSelect';
import { ExchangeRate } from '../../types/exchangeRate';
import { InvoicePreview } from '../../types/student';
import { formatMoney, formatRate, formatRateDateTime, rateOriginLabel } from '../../utils/exchangeRates';
import { RegistrationPaymentChip } from '../../components/payments/RegistrationPaymentChip';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';

const STATUS_LABELS: Record<PaymentItemStatus, string> = {
  UNPAID: 'Unpaid',
  PARTIALLY_PAID: 'Partially Paid',
  PENDING_CONFIRMATION: 'Pending Confirmation',
  PAID: 'Paid',
};

const STATUS_COLOR: Record<PaymentItemStatus, 'default' | 'warning' | 'info' | 'success'> = {
  UNPAID: 'default',
  PARTIALLY_PAID: 'warning',
  PENDING_CONFIRMATION: 'info',
  PAID: 'success',
};

const formatAmount = (amount: number | null, currency: { code: string; symbol: string | null } | null) =>
  amount == null ? '—' : `${currency?.symbol ?? currency?.code ?? ''} ${amount.toFixed(2)}`;

const formatTax = (amount: number | null) => (amount == null ? '—' : `$ ${amount.toFixed(2)}`);

const formatLkr = (amount: number | null, unavailable: boolean) => {
  if (unavailable) return 'Rate unavailable';
  if (amount == null) return '—';
  return `Rs. ${amount.toFixed(2)}`;
};

/**
 * "Payment Checklist" (Payment/Document Checklist correction, sections
 * 13-14): every active PaymentCategory's required/paid/outstanding/status
 * for this student in one table, plus a totals summary — driven entirely
 * by Master Data (PaymentCategory.amount), never hard-coded amounts.
 *
 * SSCL tax / exchange-rate payment overhaul (Phase 79): TAX and LKR
 * Calculated Amount columns added for the 3 taxable items only; every
 * other item shows "—" in TAX (never a fabricated figure), but "Total with
 * Tax" correction: that column always shows a real value — equal to
 * Student Amount itself when TAX is "—" (non-taxable). Totals
 * (Required/Paid/Outstanding) now cover only the 4 registration-
 * requirement items (base + SSCL) — every item still appears in the table
 * above, just excluded from this specific total, matching the backend's
 * own `getPaymentChecklist`. Wrapped in a horizontally-scrollable Box so
 * the extra columns don't crush the table on a narrow screen, keeping the
 * same plain-Table look everywhere else.
 */
const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

export const PaymentChecklistCard = ({ studentId }: { studentId: number }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canGenerateInvoice = hasPermission('payments', 'create');
  // Phase 104 — only Super Admin/finance may price an invoice at a manual
  // or historical rate; everyone else gets the current automatic rate.
  const canChooseRate = hasPermission('exchange_rates', 'manage');

  const query = useQuery({
    queryKey: ['payment-checklist', studentId],
    queryFn: () => getPaymentChecklist(studentId),
  });

  // Invoice generation for UNPAID/PARTIALLY PAID fee items — selection is
  // by Payment Checklist category, never requires an existing Payment
  // record. Eligible rows are those with a real outstanding balance
  // (outstandingAmount != null && > 0); a fully-paid or unconfigured item
  // has nothing to invoice and gets no checkbox at all.
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
  const [generateInvoiceOpen, setGenerateInvoiceOpen] = useState(false);
  const [generateInvoiceType, setGenerateInvoiceType] = useState<InvoiceType>('USD');
  const [generateInvoiceError, setGenerateInvoiceError] = useState<string | null>(null);
  const [generateInvoiceRate, setGenerateInvoiceRate] = useState<ExchangeRate | null>(null);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  const toggleCategorySelected = (categoryId: number) =>
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });

  const invoicePayload = () => ({
    paymentCategoryIds: Array.from(selectedCategoryIds).sort((a, b) => a - b),
    invoiceType: generateInvoiceType,
    ...(generateInvoiceType === 'LKR' && generateInvoiceRate ? { exchangeRateId: generateInvoiceRate.id } : {}),
  });

  // Phase 104 — the exact figures (rate, lines, totals, bank charges) the
  // invoice will have, shown before the user confirms; nothing is saved.
  const previewQuery = useQuery({
    queryKey: ['invoice-preview', studentId, invoicePayload()],
    queryFn: () => previewStudentInvoice(studentId, invoicePayload()),
    enabled: generateInvoiceOpen && selectedCategoryIds.size > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const closeGenerateInvoice = () => {
    setGenerateInvoiceOpen(false);
    setGenerateInvoiceError(null);
    setGenerateInvoiceRate(null);
  };

  const generateInvoiceMutation = useMutation({
    mutationFn: () => createStudentInvoice(studentId, invoicePayload()),
    onSuccess: (invoice) => {
      closeGenerateInvoice();
      setSelectedCategoryIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['payment-checklist', studentId] });
      // Invoice history lists (Documents tab, Finance workspace).
      queryClient.invalidateQueries({ queryKey: ['student-invoices', studentId] });
      setPreviewTarget({
        key: `formatted-invoice-${invoice.id}`,
        originalName: `Invoice ${invoice.invoiceNumber}.pdf`,
        mimeType: 'application/pdf',
        fetchBlob: () => getStudentInvoicePdfBlob(studentId, invoice.id),
        onDownload: async () => {
          const blob = await getStudentInvoicePdfBlob(studentId, invoice.id);
          downloadBlob(blob, `${invoice.invoiceNumber}.pdf`);
        },
      });
    },
    onError: (err) => setGenerateInvoiceError(extractErrorMessage(err)),
  });

  if (!query.data) return null;
  const { items, totalRequired, totalPaid, totalOutstanding, registrationPaymentComplete } = query.data;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Payment Checklist</Typography>
        {canGenerateInvoice && (
          <Button
            size="small"
            startIcon={<ReceiptLongIcon />}
            disabled={selectedCategoryIds.size === 0}
            onClick={() => setGenerateInvoiceOpen(true)}
          >
            Generate Invoice
          </Button>
        )}
      </Stack>

      <ExchangeRatePanel />

      <Box sx={{ overflowX: 'auto', mt: 2 }}>
        <Table size="small" sx={{ minWidth: 880 }}>
          <TableHead>
            <TableRow>
              {canGenerateInvoice && <TableCell padding="checkbox" />}
              <TableCell>Payment Item</TableCell>
              <TableCell>Required</TableCell>
              <TableCell align="right">Student Amount</TableCell>
              <TableCell align="right">TAX</TableCell>
              <TableCell align="right">Total with Tax</TableCell>
              <TableCell align="right">LKR Calculated Amount</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const eligibleForInvoice = item.outstandingAmount != null && item.outstandingAmount > 0;
              return (
                <TableRow key={item.categoryId}>
                  {canGenerateInvoice && (
                    <TableCell padding="checkbox">
                      {eligibleForInvoice && (
                        <Checkbox
                          size="small"
                          checked={selectedCategoryIds.has(item.categoryId)}
                          onChange={() => toggleCategorySelected(item.categoryId)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.isRequired ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">{formatAmount(item.requiredAmount, item.currency)}</TableCell>
                  <TableCell align="right">{formatTax(item.taxAmount)}</TableCell>
                  <TableCell align="right">{formatAmount(item.totalWithTax, item.currency)}</TableCell>
                  <TableCell align="right">{formatLkr(item.lkrAmount, item.exchangeRateUnavailable)}</TableCell>
                  <TableCell align="right">{formatAmount(item.paidAmount, item.currency)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_LABELS[item.status]} color={STATUS_COLOR[item.status]} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Typography variant="body2">
            <strong>Total Required:</strong> {totalRequired.toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Total Paid:</strong> {totalPaid.toFixed(2)}
          </Typography>
          <Typography variant="body2" color={totalOutstanding > 0 ? 'warning.main' : 'success.main'}>
            <strong>Outstanding:</strong> {totalOutstanding.toFixed(2)}
          </Typography>
          {query.data.registrationPaymentStatus ? (
            <RegistrationPaymentChip status={query.data.registrationPaymentStatus} prefix="Registration Fee" />
          ) : (
            <Chip
              size="small"
              label={registrationPaymentComplete ? 'Registration payment complete' : 'Registration payment pending'}
              color={registrationPaymentComplete ? 'success' : 'default'}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Required Total includes only Registration Fee, IMC Professional Handling Fee, Document Processing Fee and Student
          Welfare Fee (plus SSCL on the first three). Other fees above are shown for reference and don't affect this total.
        </Typography>
      </Box>

      <Dialog open={generateInvoiceOpen} onClose={closeGenerateInvoice} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedCategoryIds.size} fee item{selectedCategoryIds.size === 1 ? '' : 's'} selected.
            </Typography>
            {generateInvoiceError && <Alert severity="error">{generateInvoiceError}</Alert>}
            <RadioGroup
              row
              value={generateInvoiceType}
              onChange={(e) => setGenerateInvoiceType(e.target.value as InvoiceType)}
            >
              <FormControlLabel value="USD" control={<Radio />} label="USD Invoice" />
              <FormControlLabel value="LKR" control={<Radio />} label="LKR Invoice" />
            </RadioGroup>
            {generateInvoiceType === 'LKR' ? (
              <ExchangeRateSelect
                value={generateInvoiceRate}
                onChange={setGenerateInvoiceRate}
                canChoose={canChooseRate}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                USD pricing — the current Bank Charges from Master Data are added. No exchange rate applies.
              </Typography>
            )}
            <InvoicePreviewPanel
              loading={previewQuery.isFetching}
              error={previewQuery.isError ? extractErrorMessage(previewQuery.error) : null}
              preview={previewQuery.data ?? null}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={closeGenerateInvoice}>Cancel</Button>
          <Button
            variant="contained"
            disabled={generateInvoiceMutation.isPending || !previewQuery.data || previewQuery.isFetching}
            onClick={() => generateInvoiceMutation.mutate()}
          >
            {generateInvoiceMutation.isPending ? 'Generating…' : 'Generate PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Paper>
  );
};

/** The figures an invoice will have, from the server's own calculation
 * (Phase 104) — the rate (with its date and source) is stated first so it's
 * clear before confirming. */
const InvoicePreviewPanel = ({
  loading,
  error,
  preview,
}: {
  loading: boolean;
  error: string | null;
  preview: InvoicePreview | null;
}) => {
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!preview) return <Typography variant="body2" color="text.secondary">{loading ? 'Calculating…' : ''}</Typography>;
  const isLkr = preview.invoiceType === 'LKR';
  return (
    <Box data-testid="invoice-preview" sx={{ opacity: loading ? 0.6 : 1 }}>
      {isLkr && (
        <Alert severity={preview.exchangeRate != null ? 'info' : 'warning'} sx={{ mb: 1 }}>
          {preview.exchangeRate != null ? (
            <>
              Rate used: <strong>Rs. {formatRate(preview.exchangeRate)}</strong> per USD — effective{' '}
              {preview.exchangeRateDate ? formatRateDateTime(preview.exchangeRateDate) : '—'} —{' '}
              {rateOriginLabel(preview.exchangeRateOrigin)}
              {preview.exchangeRateOrigin === 'AUTOMATIC' ? ' (Nations Trust TT Selling)' : ''}
            </>
          ) : (
            'No exchange rate is available — LKR amounts will be blank.'
          )}
        </Alert>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">USD</TableCell>
              <TableCell align="right">SSCL (USD)</TableCell>
              {isLkr && <TableCell align="right">LKR</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.lineItems.map((li, i) => (
              <TableRow key={i}>
                <TableCell>{li.description}</TableCell>
                <TableCell align="right">{formatMoney(li.amountUsd)}</TableCell>
                <TableCell align="right">{li.taxUsd != null ? formatMoney(li.taxUsd) : '—'}</TableCell>
                {isLkr && <TableCell align="right">{li.lkrAmount != null ? formatMoney(li.lkrAmount) : '—'}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Stack spacing={0.25} sx={{ mt: 1, textAlign: 'right' }}>
        <Typography variant="body2">Subtotal: USD {formatMoney(preview.subtotalUsd)}</Typography>
        {!isLkr && (
          <Typography variant="body2">Bank Charges: USD {formatMoney(preview.bankCharges ?? 0)}</Typography>
        )}
        <Typography variant="subtitle2">
          Total: USD {formatMoney(preview.totalUsd)}
          {isLkr && preview.totalLkr != null ? ` · LKR ${formatMoney(preview.totalLkr)}` : ''}
        </Typography>
      </Stack>
    </Box>
  );
};
